import React, { useEffect, useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  InputAdornment,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import { Email as EmailIcon, Lock as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { t } from '../localization';

const AuthPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Disable page scroll while auth screen is mounted
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await onLogin({ email: formData.email, password: formData.password });
    } catch (err) {
      setError(err.response?.data?.error || 'Произошла ошибка');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        px: 2,
        py: 0,
        overflow: 'hidden',
        background: 'radial-gradient(1200px 600px at 10% 10%, rgba(14,165,233,0.18), transparent 40%), radial-gradient(1000px 500px at 90% 90%, rgba(99,102,241,0.18), transparent 40%), linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0b1320 100%)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 440,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255,255,255,0.9)',
          maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 48px)' },
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Avatar
            alt={t('appName')}
            src="/logo64.png"
            sx={{ width: 56, height: 56, mb: 1.5, bgcolor: 'transparent' }}
          />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {t('appName')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Войдите, чтобы продолжить
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error === 'Network Error' ? t('networkError') : error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            margin="normal"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Пароль"
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{
              mt: 2,
              py: 1.25,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              background: 'linear-gradient(90deg, #0ea5e9, #3b82f6)',
              boxShadow: '0 8px 20px rgba(59,130,246,0.35)',
              '&:hover': {
                background: 'linear-gradient(90deg, #0284c7, #2563eb)',
                boxShadow: '0 10px 24px rgba(37,99,235,0.45)',
              },
            }}
          >
            {isSubmitting ? 'Входим…' : 'Войти'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AuthPage;
